import css from "./MainMenu.module.css";

import {
  autoUpdate,
  flip,
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import clsx from "clsx";
import { ReactNode, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router";
import { lstring, useT, useTurl } from "~/util/Lang";
import { TrackerButton } from "./links/TrackerButton";
import { ChangeRealm } from "./ChangeRealm";

let lastId = 0;
const gid = () => {
  lastId++;
  return lastId.toString();
};

export const MENU_SEP = Symbol("MENU_SEP");

export type MenuItem = {
  id: string;
  title: lstring;
  to: string;
  submenu?: (MenuItem | typeof MENU_SEP)[];

  parent?: MenuItem;
};

export const MENU_ITEMS: ReadonlyArray<MenuItem> = [
  {
    id: "database",
    title: { en: "Database", ru: "База знаний" },
    to: "/",
    submenu: [
      {
        id: "items",
        title: { en: "Items", ru: "Предметы" },
        to: "/items",
        submenu: [
          {
            id: "weapon",
            title: { en: "Weapons", ru: "Оружие" },
            to: "/items/weapon",
          },
          {
            id: "armor",
            title: { en: "Armor", ru: "Броня" },
            to: "/items/armor",
          },
          {
            id: "shield",
            title: { en: "Shield", ru: "Щиты" },
            to: "/items/shield",
          },
          {
            id: "jewelry",
            title: { en: "Jewelry", ru: "Бижутерия" },
            to: "/items/jewelry",
          },
          MENU_SEP,
          {
            id: "sets",
            title: { en: "Item Sets", ru: "Сеты" },
            to: "/items/sets",
          },
          { id: "sa", title: { en: "Special Abilities", ru: "СА" }, to: "/sa" },
          { id: "fish", title: { en: "Fish", ru: "Рыба" }, to: "/items/fish" },
        ],
      },
      { id: gid(), title: { en: "NPCs", ru: "Мобы" }, to: "/npcs" },
      { id: gid(), title: { en: "Quests", ru: "Квесты" }, to: "/quests" },
      { id: gid(), title: { en: "Classes", ru: "Профы" }, to: "/classes" },
      { id: gid(), title: { en: "Locations", ru: "Локации" }, to: "/locs" },
    ],
  },
  {
    id: "tools",
    title: { en: "Tools", ru: "Тулзы" },
    to: "/",
    submenu: [
      {
        id: "craft",
        title: { en: "Craft", ru: "Крафт" },
        to: "/",
        submenu: [
          {
            id: "craft-shots",
            title: { en: "Calc Shots", ru: "Калькулятор шотов" },
            to: "/craft/shots",
          },
        ],
      },
      {
        id: gid(),
        title: { en: "Map", ru: "Карта" },
        to: "/map",
        submenu: [
          {
            id: "raid-bosses-map",
            title: { en: "Raid Bosses Map", ru: "Карта рейд боссов" },
            to: "/map/raid-bosses",
          },
        ],
      },
    ],
  },
  { id: gid(), title: { en: "HUD", ru: "HUD" }, to: "/hud" },
  { id: gid(), title: { en: "About Us", ru: "О нас" }, to: "/aboutus" },
];

export const MENU_MAP: Map<string, MenuItem> = new Map();

MENU_ITEMS.forEach(processMenuItem);
function processMenuItem(item: MenuItem) {
  if (MENU_MAP.has(item.id)) {
    console.warn("Duplicate menu item: " + item.id);
  }

  MENU_MAP.set(item.id, item);

  if (item.submenu) {
    for (const sub of item.submenu) {
      if (sub !== MENU_SEP) {
        sub.parent = item;
        processMenuItem(sub);
      }
    }
  }
}

export function MainMenu(props: { isHome?: boolean }) {
  const t = useT();
  const turl = useTurl();
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div
      className={clsx([css.root], "container", { [css.home]: props.isHome })}
    >
      <div className={clsx([css.menu], { [css.menuActive]: showMenu })}>
        <FloatingTree>
          <RootMenuItem menu={MENU_MAP.get("database")?.submenu ?? []}>
            <div className={css.itemDropdown}>
              <span className={css.titleDropdown}>
                {t({ en: "Database", ru: "База знаний" })}
              </span>
            </div>
          </RootMenuItem>
          <RootMenuItem menu={MENU_MAP.get("tools")?.submenu ?? []}>
            <div className={css.itemDropdown}>
              <span className={css.titleDropdown}>
                {t({ en: "Tools", ru: "Тулзы" })}
              </span>
            </div>
          </RootMenuItem>
          <NavLink
            to={turl("/hud")}
            className={(opts) =>
              clsx([css.item], { [css.itemActive]: opts.isActive })
            }
          >
            {t({ en: "HUD", ru: "HUD" })}
          </NavLink>
          <NavLink
            to={turl("/aboutus")}
            className={(opts) =>
              clsx([css.item], { [css.itemActive]: opts.isActive })
            }
          >
            {t({ en: "About Us", ru: "О нас" })}
          </NavLink>
          {DEV && <TrackerButton />}
        </FloatingTree>
      </div>
      <div className={css.realmDesktop}>
        <ChangeRealm />
      </div>
    </div>
  );
}

function RootMenuItem(props: {
  menu: ReadonlyArray<MenuItem | typeof MENU_SEP>;
  children: ReactNode;
}) {
  const nodeId = useFloatingNodeId();

  const insideTimer = useRef(0);
  const [inside, setInside_] = useState(false);
  function setInside(newInside: boolean) {
    if (insideTimer.current) {
      clearTimeout(insideTimer.current);
      insideTimer.current = 0;
    }
    if (newInside) {
      setInside_(true);
    } else {
      insideTimer.current = setTimeout(() => {
        setInside_(false);
      }, 300) as unknown as number;
    }
  }
  useEffect(() => {
    if (insideTimer.current) {
      clearTimeout(insideTimer.current);
      insideTimer.current = 0;
    }
  }, []);

  const [showTooltip, setShowTooltip] = useState(false);
  const show = showTooltip || inside;
  const { refs, context, floatingStyles } = useFloating({
    nodeId,
    open: show,
    onOpenChange: setShowTooltip,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(0), flip(), shift()],
  });

  const hover = useHover(context, {
    delay: { open: 300, close: 400 },
  });
  const dismiss = useDismiss(context, { bubbles: true });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
  ]);

  return (
    <FloatingNode id={nodeId}>
      <div>
        <div ref={refs.setReference} {...getReferenceProps()}>
          {props.children}
        </div>
        {show && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps({
                onMouseEnter: () => setInside(true),
                onMouseLeave: () => setInside(false),
              })}
            >
              <Menu menu={props.menu} />
            </div>
          </FloatingPortal>
        )}
      </div>
    </FloatingNode>
  );
}

function SubMenuItem(props: {
  open: boolean;
  setOpen: (open: boolean) => void;
  menu: (MenuItem | typeof MENU_SEP)[];
  children: ReactNode;
}) {
  const nodeId = useFloatingNodeId();

  const { refs, context, floatingStyles } = useFloating({
    nodeId,
    open: props.open,
    onOpenChange: props.setOpen,
    placement: "right-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(0), flip(), shift()],
  });

  const hover = useHover(context, {
    delay: { open: 100, close: 400 },
  });
  const dismiss = useDismiss(context, { bubbles: true });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
  ]);

  return (
    <FloatingNode id={nodeId}>
      <div>
        <div ref={refs.setReference} {...getReferenceProps()}>
          {props.children}
        </div>
        {props.open && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <Menu menu={props.menu} />
            </div>
          </FloatingPortal>
        )}
      </div>
    </FloatingNode>
  );
}

function Menu(props: {
  menu: ReadonlyArray<MenuItem | typeof MENU_SEP>;
  placement?: Placement;
}) {
  const t = useT();
  const turl = useTurl();

  const [cur, setCur] = useState("");

  return (
    <div>
      <div className={css.menuDropdown}>
        {props.menu.map((item, i) => {
          if (item === MENU_SEP) {
            return <div key={i} className={css.menuSep} />;
          }

          if (item.submenu) {
            return (
              <SubMenuItem
                key={i}
                open={cur === item.id}
                setOpen={(open) => {
                  if (open) {
                    setCur(item.id);
                  }
                }}
                menu={item.submenu}
              >
                <NavLink
                  to={turl(item.to)}
                  className={(opts) =>
                    clsx([css.item], {
                      [css.itemActive]: opts.isActive,
                      [css.itemHover]: cur === item.id,
                    })
                  }
                >
                  {t(item.title)}
                  <div className={css.arrowRight} />
                </NavLink>
              </SubMenuItem>
            );
          }
          return (
            <NavLink
              key={i}
              to={turl(item.to)}
              className={(opts) =>
                clsx([css.item], {
                  [css.itemActive]: opts.isActive,
                  [css.itemHover]: cur === item.id,
                })
              }
              onMouseEnter={() => setCur(item.id)}
              onMouseLeave={() => setCur("")}
            >
              {t(item.title)}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}
